import { Add, Person } from '@mui/icons-material';
import { Box, useMediaQuery } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment/moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { Enum } from '~/data/enums';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  DynamicPersonnelCustomParams,
  DynamicPersonnelIncludeOptionalData,
  useDynamicPersonnelReportData,
} from '~/data/reports';
import {
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';
import {
  ReportDataExport,
  ReportExportOptions,
} from '~/scenes/PortalModules/Analytics/common/ReportDataExport';
import { PersonnelListCustomOptionalDataSelector } from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport/components/PersonnelListCustomOptionalDataSelector';
import {
  DynamicPersonnelReportSelectorCustomParams,
  usePersonnelListCustomReport,
} from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport/usePersonnelListCustomReport';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport/useStyles';

export const PersonnelListCustomReport = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const isSmallScreen = useMediaQuery('(max-height:650px)');

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const apiRef = useGridApiRef();

  const defaultReportSelection: AnalyticsReportProps<DynamicPersonnelReportSelectorCustomParams> =
    {
      reportType: {
        type: AnalyticsReportTypes.Team,
        role: AnalyticsReportRoleScope.Manager,
        employeeId: loggedInEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        startDate: moment().toDate(),
        endDate: moment().toDate(),
        empScope: AnalyticsReportEmployeeScope.AllEmployees,
        optionalData: [],
      },
    };

  const { optionalDataOptions } = useMemo(() => {
    const optionalDataOptions: Enum[] = [
      {
        code: DynamicPersonnelIncludeOptionalData.AdditionalPersonnelGroups,
        name: t('additionalPersonnelGroups'),
      },
      {
        code: DynamicPersonnelIncludeOptionalData.Salaries,
        name: t('laBaseSalary'),
      },
      {
        code: DynamicPersonnelIncludeOptionalData.TotalCompensation,
        name: `${t('laAllowance')} & ${t('laBenefits')}`,
      },
      {
        code: DynamicPersonnelIncludeOptionalData.WorkingHours,
        name: t('laWorkingHours'),
      },
      {
        code: DynamicPersonnelIncludeOptionalData.AdditionalTrees,
        name: t('secondaryTree'),
      },
    ];

    return {
      optionalDataOptions,
    };
  }, [t]);

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
      },
    },
  });

  const reportRequestProps =
    useMemo((): AnalyticsReportProps<DynamicPersonnelCustomParams> => {
      const { params, ...rest } = reportDatasetProps.reportSelection;

      if (!!params) {
        const { optionalData, ...paramsRest } = params;

        const includeOptionalData = Object.fromEntries(
          optionalData.map((param: string) => [param, true]),
        );

        return {
          ...rest,
          params: {
            ...paramsRest,
            ...includeOptionalData,
          },
        };
      }

      return rest;
    }, [reportDatasetProps.reportSelection]);

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useDynamicPersonnelReportData<DynamicPersonnelCustomParams>(
    reportRequestProps,
  );

  const { table, isLoading: isLoadingDependencies } =
    usePersonnelListCustomReport({
      data,
      reportSelection: reportDatasetProps.reportSelection,
    });

  return (
    <ReportViewLayout
      heading={t('laCustomPersonelList')}
      isLoading={isLoadingDependencies}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  const currentOptionalData =
                    params?.optionalData.map((od) => `${od}`) ?? [];

                  return (
                    <>
                      {`${moment(params?.startDate).format('L')}`}
                      {currentOptionalData.length > 0 ? (
                        <Box>
                          <Add fontSize={'inherit'} color={'inherit'} />
                          {optionalDataOptions
                            .filter((od) =>
                              currentOptionalData.includes(od.code),
                            )
                            .map((od) => od.name)
                            .join(', ')}
                        </Box>
                      ) : null}
                    </>
                  );
                }}
                renderParamsFields={({ params, onChange }) => {
                  return (
                    <Box sx={sxStyles.paramsLayout}>
                      <ReportDateRangeSelector
                        startDate={params?.startDate}
                        endDate={params?.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({ ...params, startDate, endDate })
                        }
                        minDate={moment()
                          .startOf('year')
                          .subtract(9, 'years')
                          .toDate()}
                        maxDate={moment().toDate()}
                        mode={ReportDateRangeSelectorMode.SingleDateRange}
                      />
                      <PersonnelListCustomOptionalDataSelector
                        label={t('optionalData')}
                        value={(params?.optionalData ?? []) as string[]}
                        options={optionalDataOptions}
                        onChange={(optionalData) => {
                          const validOptions = Object.values(
                            DynamicPersonnelIncludeOptionalData,
                          ).map((opt) => opt.toString());

                          onChange({
                            ...params,
                            optionalData: optionalData.filter((option) =>
                              validOptions.includes(option),
                            ) as DynamicPersonnelIncludeOptionalData[],
                          });
                        }}
                      />
                    </Box>
                  );
                }}
              />
              <ReportDatasetRowCount
                count={table.rows.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <ReportDataExport
              title={t('laCustomPersonelList')}
              startDate={reportDatasetProps.reportSelection.params?.startDate}
              apiRef={apiRef}
              exportOptions={[
                ReportExportOptions.Excel,
                ReportExportOptions.CSV,
              ]}
            />
          ),
        },
      }}
      slotProps={{
        content: {
          sx: {
            minHeight: isSmallScreen ? 'calc(100vh - 100px)' : 0,
          },
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        getRowId={(row) => row.rowId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
        columnHeaderHeight={36}
      />
    </ReportViewLayout>
  );
};
